import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useRest } from "@/composables/useRest";

const buildSifcopListEndpoint = () => {
  const base = String(process.env.VUE_APP_BACKEND_ENVIRONMENT || "").replace(/\/$/, "");
  const sifcopBase = base.replace(/\/api\/v1(?=\/|$)/i, "/api/v3");
  return `${sifcopBase}/sifcop`;
};

const LIST_ENDPOINT = buildSifcopListEndpoint();

const defaultFilters = () => ({
  fecha: null,
  fechaDesde: null,
  fechaHasta: null,
  identificacion: "",
  departamental: null,
});

const formatDate = (value) => {
  if (!value) return "";

  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

const normalizeDepartamentalOptions = (options = []) =>
  (Array.isArray(options) ? options : []).map((option) => {
    if (typeof option === "string") {
      return { label: option, value: option };
    }

    return {
      label: option?.label || option?.nombre || option?.name || option?.departamental || option?.value,
      value: option?.value || option?.id || option?.departamental || option?.nombre || option?.name,
    };
  });

const buildDepartamentalOptions = (records = []) =>
  normalizeDepartamentalOptions([...new Set(records.map((item) => item.departamental))]);

const normalizeRemoteRecord = (record = {}) => ({
  id: record?.id ?? record?.ActaSifcopID ?? null,
  fecha: record?.fecha ?? record?.Fecha ?? "",
  identificacion: record?.identificacion ?? record?.Identificador ?? "",
  departamental: record?.departamental ?? record?.Departamento ?? "",
  dependencia: record?.dependencia ?? record?.DependenciaPolicial ?? "",
});

const normalizeRemoteRecords = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => normalizeRemoteRecord(row));

export const useSifcopListStore = defineStore("sifcopList", () => {
  const records = ref([]);
  const items = ref([]);
  const isLoading = ref(false);
  const filterOptionsLoading = ref(false);
  const filters = ref(defaultFilters());
  const pageSize = ref(5);
  const currentPage = ref(1);
  const departamentalOptions = ref([]);

  const rest = useRest();

  const normalizedIdentification = computed(() =>
    String(filters.value.identificacion || "")
      .trim()
      .toLowerCase()
  );

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);

  const filteredItems = computed(() => {
    return records.value.filter((item) => {
      const matchesFecha = !filters.value.fecha || item.fecha === filters.value.fecha;
      const matchesDesde = !filters.value.fechaDesde || item.fecha >= filters.value.fechaDesde;
      const matchesHasta = !filters.value.fechaHasta || item.fecha <= filters.value.fechaHasta;
      const matchesIdentificacion =
        !normalizedIdentification.value ||
        item.identificacion.toLowerCase().includes(normalizedIdentification.value);
      const matchesDepartamental =
        !filters.value.departamental || item.departamental === filters.value.departamental;

      return (
        matchesFecha &&
        matchesDesde &&
        matchesHasta &&
        matchesIdentificacion &&
        matchesDepartamental
      );
    });
  });

  const totalRecords = computed(() => filteredItems.value.length);
  const totalPages = computed(() => Math.ceil(totalRecords.value / pageSize.value));

  const paginatedItems = computed(() => filteredItems.value.slice(offset.value, offset.value + pageSize.value));

  const activeFilterChips = computed(() => {
    const chips = [];

    if (filters.value.fecha) {
      chips.push({ key: "fecha", label: `Fecha: ${formatDate(filters.value.fecha)}` });
    }

    if (filters.value.fechaDesde || filters.value.fechaHasta) {
      const desde = filters.value.fechaDesde ? formatDate(filters.value.fechaDesde) : "-";
      const hasta = filters.value.fechaHasta ? formatDate(filters.value.fechaHasta) : "-";
      chips.push({ key: "rango", label: `Rango: ${desde} - ${hasta}` });
    }

    if (normalizedIdentification.value) {
      chips.push({
        key: "identificacion",
        label: `Identificación: ${filters.value.identificacion.trim()}`,
      });
    }

    if (filters.value.departamental) {
      chips.push({ key: "departamental", label: `Departamental: ${filters.value.departamental}` });
    }

    return chips;
  });

  const resetPage = () => {
    currentPage.value = 1;
  };

  const setItems = (nextRecords = []) => {
    const normalizedRecords = Array.isArray(nextRecords) ? nextRecords : [];
    records.value = normalizedRecords;
    items.value = normalizedRecords;
    setDepartamentalOptions(buildDepartamentalOptions(normalizedRecords));
    resetPage();
  };

  const setDepartamentalOptions = (options = []) => {
    departamentalOptions.value = normalizeDepartamentalOptions(options);
  };

  const applyRemoteResponse = (response = {}) => {
    const payload = response?.data || response;
    const nextItems = normalizeRemoteRecords(payload?.data || payload?.items || payload || []);

    records.value = Array.isArray(nextItems) ? nextItems : [];
    items.value = Array.isArray(nextItems) ? nextItems : [];
    setDepartamentalOptions(buildDepartamentalOptions(items.value));
  };

  const fetchItems = async () => {
    isLoading.value = true;

    try {
      const response = await rest.get({ url: LIST_ENDPOINT });
      applyRemoteResponse(response?.data || response);
    } catch (error) {
      console.error("Error fetching SIFCOP items", error);
      return items.value;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchFilterOptions = async () => {
    filterOptionsLoading.value = true;

    try {
      if (!items.value.length) {
        await fetchItems();
      }

      setDepartamentalOptions(buildDepartamentalOptions(items.value));
    } catch (error) {
      console.error("Error fetching SIFCOP filter options", error);
      return departamentalOptions.value;
    } finally {
      filterOptionsLoading.value = false;
    }
  };

  const setFilters = async (nextFilters = {}) => {
    filters.value = {
      ...defaultFilters(),
      ...nextFilters,
      fecha: nextFilters.fecha || null,
      fechaDesde: nextFilters.fechaDesde || null,
      fechaHasta: nextFilters.fechaHasta || null,
      identificacion: nextFilters.identificacion || "",
      departamental: nextFilters.departamental || null,
    };
    resetPage();

  };

  const clearFilters = async () => {
    filters.value = defaultFilters();
    resetPage();

  };

  const removeFilter = async (key) => {
    if (key === "rango") {
      filters.value = {
        ...filters.value,
        fechaDesde: null,
        fechaHasta: null,
      };
      resetPage();

      return;
    }

    filters.value = {
      ...filters.value,
      [key]: key === "identificacion" ? "" : null,
    };
    resetPage();

  };

  const goToPage = async (page) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;

    }
  };

  const prevPage = async () => {
    if (currentPage.value > 1) {
      await goToPage(currentPage.value - 1);
    }
  };

  const nextPage = async () => {
    if (currentPage.value < totalPages.value) {
      await goToPage(currentPage.value + 1);
    }
  };

  const handlePageChange = async ({ page, rows }) => {
    currentPage.value = page + 1;
    pageSize.value = rows;

  };

  return {
    items,
    isLoading,
    filterOptionsLoading,
    filters,
    pageSize,
    currentPage,
    offset,
    totalRecords,
    totalPages,
    filteredItems,
    paginatedItems,
    departamentalOptions,
    activeFilterChips,
    setItems,
    setDepartamentalOptions,
    fetchItems,
    fetchFilterOptions,
    setFilters,
    clearFilters,
    removeFilter,
    goToPage,
    prevPage,
    nextPage,
    handlePageChange,
  };
});
